import { ReactNode, useEffect, useState } from "react";

import { StyleProvider } from "@ant-design/cssinjs";
import isPropValid from "@emotion/is-prop-valid";
import { App as AntApp, ConfigProvider, ThemeConfig } from "antd";
import { StyleSheetManager } from "styled-components";

import SpinLoader from "@/containers/Layout/MainLayout/SpinLoader";
import { useConfigurationQuery } from "@/services";
import { setModalApi } from "@/services/helpers/antdApp";

import baseTheme from "@/themes/baseTheme";
import devTheme from "@/themes/devTheme";
import stageTheme from "@/themes/stageTheme";
import testTheme from "@/themes/testTheme";
import { Environment } from "./types";

interface IProps {
  children: ReactNode;
}

function shouldForwardProp(propName: string, target: unknown): boolean {
  if (typeof target === "string") {
    return isPropValid(propName);
  }
  return true;
}

const AntdStaticBridge = () => {
  const { modal } = AntApp.useApp();

  useEffect((): void => {
    setModalApi(modal);
  }, [modal]);

  return null;
};

const SiteWrapper = ({ children }: IProps) => {
  const { data: config } = useConfigurationQuery({});
  const [environment, setEnvironment] = useState<Environment>("production");
  const [theme, setTheme] = useState<ThemeConfig>(baseTheme);

  useEffect((): void => {
    if (!config) return;

    setEnvironment(config.environment);
  }, [config]);

  useEffect((): void => {
    switch (environment) {
      case "dev":
        setTheme(devTheme);
        break;
      case "test":
        setTheme(testTheme);
        break;
      case "staging":
        setTheme(stageTheme);
        break;
      default:
        setTheme(baseTheme);
        break;
    }
  }, [environment]);

  if (!config) {
    return <SpinLoader />;
  }

  return (
    <ConfigProvider theme={theme}>
      <AntApp>
        <AntdStaticBridge />
        <StyleSheetManager shouldForwardProp={shouldForwardProp}>
          <StyleProvider hashPriority="high">{children}</StyleProvider>
        </StyleSheetManager>
      </AntApp>
    </ConfigProvider>
  );
};

export default SiteWrapper;
